import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { IconStar, IconBrandWindowsFilled } from '@tabler/icons-react';

export const HybridRecipientManagement = () => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [inputs, setInputs] = useState({});
    const [inputErrors, setInputErrors] = useState({});
    const [routingDomain, setRoutingDomain] = useState('');
    const [recentScenarios, setRecentScenarios] = useState([]);
    const [pendingEntry, setPendingEntry] = useState(null);

    const HISTORY_KEY = 'recentScenarios';
    const LAST_KEY = 'lastScenario';
    const ROUTING_KEY = 'routingDomain';

    const validateAllFields = (newInputs, routingVal = routingDomain) => {
        const newErrors = {};

        if (routingVal.trim() && (!routingVal.includes('.') || !routingVal.endsWith('.mail.onmicrosoft.com'))) {
            newErrors.__routing = 'Invalid format. Example: toolify365.mail.onmicrosoft.com';
        }

        if (selectedScenario?.placeholders) {
            selectedScenario.placeholders.forEach((ph) => {
                const val = newInputs[ph] || '';
                const err = validateField(ph, val);
                if (err) newErrors[ph] = err;
            });
        }

        setInputErrors(newErrors);
    };

    const validateField = (placeholder, value) => {
        let error = null;

        if (!value?.trim()) return null;

        if (
            ['<UserEmail>', '<AddEmail>', '<RemoveEmail>', '<ExternalEmail>', '<GroupEmail>', '<Mailbox>'].includes(placeholder) &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ) {
            error = 'Invalid email address.';
        }

        if (placeholder === '<Alias>' && !/^[a-zA-Z0-9]+$/.test(value)) {
            error = 'Alias must contain only letters and numbers (no spaces or special characters).';
        }

        if (placeholder === '<RoomOrEquip>') {
            const validTypes = ['Room', 'Equipment'];
            if (!validTypes.includes(value.trim())) {
                error = 'RoomOrEquip must be either "Room" or "Equipment".';
            }
        }

        if (placeholder === '<AccessRights>') {
            const validRights = [
                'FullAccess',
                'ReadPermission',
                'DeleteItem',
                'Reviewer',
                'Editor',
                'AvailabilityOnly'
            ];
            if (!validRights.includes(value)) {
                error = `AccessRights must be one of the following: ${validRights.join(', ')}`;
            }
        }

        if (placeholder === '<CalendarName>' && !/^[\w/-]+$/.test(value)) {
            error = 'CalendarName may only contain letters, numbers, "-", and "/".';
        }

        if (placeholder === '<ADUser>' && !/^[\w-.]+$/.test(value)) {
            error = 'ADUser may only contain letters, numbers, "-", "_" and ".".';
        }

        if (placeholder === '<DisplayName>' && value.length < 3) {
            error = 'DisplayName is too short.';
        }

        if (placeholder === '<GroupName>' && value.length < 3) {
            error = 'GroupName is too short.';
        }

        if (placeholder === '<PolicyName>' && value.length < 3) {
            error = 'PolicyName is too short.';
        }

        if (placeholder === '<Priority>' && !/^\d+$/.test(value)) {
            error = 'Priority must be an integer.';
        }

        if (placeholder === '<Company>' && value.length < 2) {
            error = 'Company name is too short.';
        }

        if (placeholder === '<Recipients>') {
            const valid = ['MailboxUsers', 'MailContacts', 'AllRecipients'];
            if (!valid.includes(value)) {
                error = `Recipients must be one of the following: ${valid.join(', ')}`;
            }
        }

        return error;
    };


    const allInputsValidOrEmpty = () => {
        const placeholdersValid = selectedScenario.placeholders.every((ph) => {
            const val = inputs[ph] || '';
            const err = inputErrors[ph];
            return val === '' || !err;
        });

        const routingValid = routingDomain.trim() === '' || !inputErrors.__routing;

        return placeholdersValid && routingValid;
    };

    const scenarios = [
        // Distribution Group
        {
            category: 'Distribution Group',
            title: 'Distribution Group: Change Alias (Add/Remove)',
            description: 'Add/remove email addresses on Distribution Group.',
            commandTemplate: 'Set-DistributionGroup <GroupEmail> -EmailAddresses @{add="<AddEmail>"}\nSet-DistributionGroup <GroupEmail> -EmailAddresses @{remove="<RemoveEmail>"}',
            placeholders: ['<GroupEmail>', '<AddEmail>', '<RemoveEmail>'],
            placeholdersText: {
                '<GroupEmail>': 'e.g., support@toolify.at',
                '<AddEmail>': 'e.g., support-team@toolify.at',
                '<RemoveEmail>': 'e.g., old-support@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-distributiongroup?view=exchange-ps'
        },
        {
            category: 'Distribution Group',
            title: 'Distribution Group: Manage Distribution Group Members',
            description: 'Add and remove members from a distribution group.',
            commandTemplate:
                'Add-DistributionGroupMember -Identity <Group> -Member <UserEmail>\nRemove-DistributionGroupMember -Identity <Group> -Member <UserEmail>',
            placeholders: ['<GroupEmail>', '<UserEmail>'],
            placeholdersText: {
                '<GroupEmail>': 'e.g., support@toolify.at',
                '<UserEmail>': 'e.g., john.doe@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/add-distributiongroupmember?view=exchange-ps'
        },
        {
            category: 'Distribution Group',
            title: 'Distribution Group: New Distribution Group',
            description: 'Create a new distribution group.',
            commandTemplate: 'New-DistributionGroup -Name "<GroupName>" -Alias <Alias> -PrimarySmtpAddress <GroupEmail>',
            placeholders: ['<GroupName>', '<Alias>', '<GroupEmail>'],
            placeholdersText: {
                '<GroupName>': 'e.g., Support-Team',
                '<Alias>': 'e.g., support',
                '<GroupEmail>': 'e.g., support@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/new-distributiongroup?view=exchange-ps'
        },
        // Email Address Policy
        {
            category: 'Email Address Policy',
            title: 'Email Address Policy: Get Email Address Policy',
            description: 'View email address policies and their configuration.',
            commandTemplate: 'Get-EmailAddressPolicy | Format-Table Name, Priority, RecipientFilter, EnabledEmailAddressTemplates',
            placeholders: [],
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/get-emailaddresspolicy?view=exchange-ps'
        },
        {
            category: 'Email Address Policy',
            title: 'Email Address Policy: Set Email Address Policy',
            description: 'Modify email address policy settings for Exchange Online.',
            commandTemplate:
                'Set-EmailAddressPolicy -Identity "<PolicyName>" -EnabledEmailAddressTemplates "<SMTPTemplate>" -Priority <Priority> -ConditionalCompany "<Company>" -IncludedRecipients <Recipients>',
            placeholders: ['<PolicyName>', '<SMTPTemplate>', '<Priority>', '<Company>', '<Recipients>'],
            placeholdersText: {
                '<PolicyName>': 'e.g., DefaultPolicy',
                '<SMTPTemplate>': 'e.g., SMTP:@toolify.at',
                '<Priority>': 'e.g., 1',
                '<Company>': 'e.g., Toolify GmbH',
                '<Recipients>': 'e.g., MailboxUsers, MailContacts'
            },
            examples: [
                {
                    placeholder: '<SMTPTemplate>',
                    label: 'Default SMTP for toolify.at',
                    value: 'SMTP:@toolify.at'
                },
                {
                    placeholder: '<Recipients>',
                    label: 'Mailbox users only',
                    value: 'MailboxUsers'
                },
                {
                    placeholder: '<Recipients>',
                    label: 'All recipients',
                    value: 'AllRecipients'
                },
                {
                    placeholder: '<Recipients>',
                    label: 'Mail contacts only',
                    value: 'MailContacts'
                }
            ],
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-emailaddresspolicy?view=exchange-ps'
        },
        // Mail Public Folder
        {
            category: 'Mail Public Folder',
            title: 'Mail Public Folder: Change Alias (Add/Remove)',
            description: 'Add/remove email addresses on Mail-Enabled Public Folder.',
            commandTemplate: 'Set-MailPublicFolder <FolderEmail> -EmailAddresses @{add="<AddEmail>"}\nSet-MailPublicFolder <FolderEmail> -EmailAddresses @{remove="<RemoveEmail>"}',
            placeholders: ['<FolderEmail>', '<AddEmail>', '<RemoveEmail>'],
            placeholdersText: {
                '<FolderEmail>': 'e.g., publicfolder@toolify.at',
                '<AddEmail>': 'e.g., pf-alias@toolify.at',
                '<RemoveEmail>': 'e.g., old-pf@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-mailpublicfolder?view=exchange-ps'
        },
        // Mailbox Resource
        {
            category: 'Mailbox Resource',
            title: 'Mailbox Resource: New Resource Mailbox',
            description: 'Create resource mailbox (Room/Equipment).',
            commandTemplate: 'New-RemoteMailbox <Mailbox> -RemoteRoutingAddress <RoutingAddress> -<RoomOrEquip>',
            placeholders: ['<Mailbox>', '<RoomOrEquip>'],
            placeholdersText: {
                '<Mailbox>': 'e.g., beamer01@toolify.at',
                '<RoomOrEquip>': 'Select: Room or Equipment'
            },
            examples: [
                { placeholder: '<RoomOrEquip>', label: 'Room', value: 'Room' },
                { placeholder: '<RoomOrEquip>', label: 'Equipment', value: 'Equipment' }
            ],
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/new-remotemailbox?view=exchange-ps'
        },
        {
            category: 'Mailbox Resource',
            title: 'Mailbox Resource: Change Resource Mailbox Settings',
            description: 'Adjust calendar subject behavior for resource mailboxes (Room or Equipment).',
            commandTemplate: 'Get-Mailbox <Mailbox> | Where-Object {$_.ResourceType -eq "<RoomOrEquip>"} | Set-CalendarProcessing -DeleteSubject $False -AddOrganizerToSubject $True',
            placeholders: ['<Mailbox>', '<RoomOrEquip>'],
            placeholdersText: {
                '<Mailbox>': 'e.g., beamer01@toolify.at',
                '<RoomOrEquip>': 'Select: Room or Equipment'
            },
            examples: [
                { placeholder: '<RoomOrEquip>', label: 'Room', value: 'Room' },
                { placeholder: '<RoomOrEquip>', label: 'Equipment', value: 'Equipment' }
            ],
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-calendarprocessing?view=exchange-ps'
        },
        // Mailbox Shared
        {
            category: 'Mailbox Shared',
            title: 'Mailbox Shared: Change Alias (Add/Remove)',
            description: 'Add/remove email addresses on Shared Mailbox.',
            commandTemplate: 'Set-RemoteMailbox <SharedEmail> -EmailAddresses @{add="<AddEmail>"}\nSet-RemoteMailbox <SharedEmail> -EmailAddresses @{remove="<RemoveEmail>"}',
            placeholders: ['<SharedEmail>', '<AddEmail>', '<RemoveEmail>'],
            placeholdersText: {
                '<SharedEmail>': 'e.g., shared.info@toolify.at',
                '<AddEmail>': 'e.g., info@toolify.at',
                '<RemoveEmail>': 'e.g., old-info@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-remotemailbox?view=exchange-ps'
        },
        {
            category: 'Mailbox Shared',
            title: 'Mailbox Shared: Create Shared Mailbox',
            description: 'Enable shared RemoteMailbox.',
            commandTemplate: 'Enable-RemoteMailbox <UserEmail> -RemoteRoutingAddress <RoutingAddress> -Shared -Alias <Alias>',
            placeholders: ['<UserEmail>', '<Alias>'],
            placeholdersText: {
                '<UserEmail>': 'e.g., info@toolify.at',
                '<Alias>': 'e.g., info'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/enable-remotemailbox?view=exchange-ps'
        },
        // Mailbox User
        {
            category: 'Mailbox User',
            title: 'Mailbox User: Change Alias (Add/Remove)',
            description: 'Add/remove alias on RemoteMailbox.',
            commandTemplate: 'Set-RemoteMailbox <UserEmail> -EmailAddresses @{add="<AddEmail>"}\nSet-RemoteMailbox <UserEmail> -EmailAddresses @{remove="<RemoveEmail>"}',
            placeholders: ['<UserEmail>', '<AddEmail>', '<RemoveEmail>'],
            placeholdersText: {
                '<UserEmail>': 'e.g., john.doe@toolify.at',
                '<AddEmail>': 'e.g., john.add@toolify.at',
                '<RemoveEmail>': 'e.g., john.remove@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-remotemailbox?view=exchange-ps'
        },
        {
            category: 'Mailbox User',
            title: 'Mailbox User: Change User to Shared',
            description: 'Convert mailbox to shared.',
            commandTemplate: 'Set-RemoteMailbox <UserEmail> -Type Shared',
            placeholders: ['<UserEmail>'],
            placeholdersText: {
                '<UserEmail>': 'e.g., john.doe@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-remotemailbox?view=exchange-ps'
        },
        {
            category: 'Mailbox User',
            title: 'Mailbox User: Default Calendar Rights',
            description: 'Set default calendar permissions.',
            commandTemplate: 'Set-MailboxFolderPermission -Identity <Mailbox>:\\<CalendarName> -User Default -AccessRights <AccessRights>',
            placeholders: ['<Mailbox>', '<CalendarName>', '<AccessRights>'],
            examples: [
                { placeholder: '<AccessRights>', label: 'Reviewer (read only)', value: 'Reviewer' },
                { placeholder: '<AccessRights>', label: 'Editor (read/write)', value: 'Editor' },
                { placeholder: '<AccessRights>', label: 'AvailabilityOnly', value: 'AvailabilityOnly' }
            ],
            placeholdersText: {
                '<Mailbox>': 'e.g., room101@toolify.at',
                '<CalendarName>': 'e.g., Kalender/Calendar',
                '<AccessRights>': 'e.g., Reviewer/Editor/...'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-mailboxfolderpermission?view=exchange-ps'
        },
        {
            category: 'Mailbox User',
            title: 'Mailbox User: Grant Mailbox Permissions',
            description: 'Grant mailbox permissions.',
            commandTemplate: 'Add-MailboxPermission -Identity <Mailbox> -User <UserEmail> -AccessRights <AccessRights> -InheritanceType All -AutoMapping $true',
            placeholders: ['<Mailbox>', '<UserEmail>', '<AccessRights>'],
            examples: [
                { placeholder: '<AccessRights>', label: 'Full Access', value: 'FullAccess' },
                { placeholder: '<AccessRights>', label: 'Read Only', value: 'ReadPermission' },
                { placeholder: '<AccessRights>', label: 'Delete Item', value: 'DeleteItem' }
            ],
            placeholdersText: {
                '<Mailbox>': 'e.g., shared.support@toolify.at',
                '<UserEmail>': 'e.g., john.doe@toolify.at',
                '<AccessRights>': 'e.g., FullAccess/Read Only/...'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/add-mailboxpermission?view=exchange-ps'
        },
        {
            category: 'Mailbox User',
            title: 'Mailbox User: New Mail User',
            description: 'Create a mail-enabled user (no mailbox).',
            commandTemplate: 'New-MailUser -Name "<DisplayName>" -ExternalEmailAddress <ExternalEmail> -UserPrincipalName <UserEmail> -Alias <Alias>',
            placeholders: ['<DisplayName>', '<ExternalEmail>', '<UserEmail>', '<Alias>'],
            placeholdersText: {
                '<DisplayName>': 'e.g., John Doe (ext)',
                '<ExternalEmail>': 'e.g., john@externaldomain.at',
                '<UserEmail>': 'e.g., john.doe@toolify.at',
                '<Alias>': 'e.g., john.doe'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/new-mailuser?view=exchange-ps'
        },
        {
            category: 'Mailbox User',
            title: 'Mailbox User: Remove Remote Mailbox',
            description: 'Remove a RemoteMailbox object.',
            commandTemplate: 'Remove-RemoteMailbox <UserEmail> -Confirm:$false',
            placeholders: ['<UserEmail>'],
            placeholdersText: {
                '<UserEmail>': 'e.g., john.doe@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/powershell/module/exchange/remove-remotemailbox?view=exchange-ps'

        },
        {
            category: 'Mailbox User',
            title: 'Mailbox User: User Creation',
            description: 'Enable RemoteMailbox for new user.',
            commandTemplate: 'Enable-RemoteMailbox <UserEmail> -RemoteRoutingAddress <RoutingAddress> -Alias <Alias>',
            placeholders: ['<UserEmail>', '<Alias>'],
            placeholdersText: {
                '<UserEmail>': 'e.g., john.doe@toolify.at',
                '<Alias>': 'e.g., john.doe'
            },
            learnLink: 'https://learn.microsoft.com/de-de/powershell/module/exchange/enable-remotemailbox?view=exchange-ps'
        },
        {
            category: 'Mailbox User',
            title: 'Mailbox User: User Offboarding',
            description: 'Disable user in AD, convert mailbox to shared, and remove all Office 365 licenses using Microsoft Graph.',
            commandTemplate:
                '### Step 1: Disable user in Active Directory\n' +
                'Disable-ADAccount -Identity <ADUser>    # disables the AD user account\n\n' +
                '### Step 2: Convert mailbox to shared\n' +
                'Set-RemoteMailbox <UserEmail> -Type Shared    # converts the mailbox to shared type\n\n' +
                '### Step 3: Connect to Microsoft Graph\n' +
                'Connect-MgGraph -Scopes "User.ReadWrite.All", "Organization.Read.All"    # connect with required Graph scopes\n\n' +
                '### Step 4: Remove all Office 365 licenses\n' +
                '$UserObject = Get-MgUser -UserId <UserEmail>    # get user object by UPN\n' +
                '$AssignedLicenses = (Get-MgUserLicenseDetail -UserId $UserObject.Id).SkuId    # get all assigned license SkuIds\n' +
                'Set-MgUserLicense -UserId $UserObject.Id -AddLicenses @() -RemoveLicenses $AssignedLicenses    # remove all licenses',
            placeholders: ['<ADUser>', '<UserEmail>'],
            placeholdersText: {
                '<ADUser>': 'e.g., SAM Account Name "john.doe"',
                '<UserEmail>': 'e.g., john.doe@toolify.at'
            },
            learnLink: 'https://learn.microsoft.com/en-us/microsoft-365/admin/add-users/remove-former-employee?view=o365-worldwide'
        },
    ];

    // Extrahiere einzigartige Kategorien und sortiere alphabetisch
    const categories = useMemo(() => {
        return [...new Set(scenarios.map(s => s.category))].sort().map(cat => ({
            value: cat,
            label: cat
        }));
    }, []);

    // Filtere Szenarien basierend auf ausgewählter Kategorie
    const filteredScenarios = useMemo(() => {
        if (!selectedCategory) return [];

        return scenarios
            .filter(s => s.category === selectedCategory.value)
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((scenario) => {
                // Entferne den Kategorie-Präfix aus dem Titel für die Anzeige
                const displayTitle = scenario.title.replace(`${scenario.category}: `, '');
                return {
                    value: scenario.title,
                    description: scenario.description,
                    title: scenario.title,
                    commandTemplate: scenario.commandTemplate,
                    placeholders: scenario.placeholders,
                    placeholdersText: scenario.placeholdersText,
                    examples: scenario.examples || [],
                    label: displayTitle,
                    learnLink: scenario.learnLink,
                    category: scenario.category,
                };
            });
    }, [selectedCategory]);

    const updateRecentScenarios = (title, inputs = {}, routingDomain = '') => {
        const history = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');

        const filtered = history.filter(
            (entry) =>
                entry.title !== title ||
                JSON.stringify(entry.inputs) !== JSON.stringify(inputs) ||
                entry.routingDomain !== routingDomain
        );

        const updated = [{ title, inputs, routingDomain }, ...filtered].slice(0, 12);

        localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
        setRecentScenarios(updated);
    };

    const getRecentScenarios = () => {
        return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    };

    useEffect(() => {
        const last = localStorage.getItem(LAST_KEY);
        const recent = getRecentScenarios();
        const storedRouting = localStorage.getItem(ROUTING_KEY);

        setRecentScenarios(recent);

        if (last && categories.length > 0) {
            const scenario = scenarios.find((s) => s.title === last);
            if (scenario) {
                // Setze die Kategorie automatisch
                const category = categories.find(cat => cat.value === scenario.category);
                if (category) {
                    setSelectedCategory(category);
                }

                // Warte kurz damit filteredScenarios aktualisiert wird
                setTimeout(() => {
                    // Entferne den Kategorie-Präfix aus dem Label
                    const displayTitle = scenario.title.replace(`${scenario.category}: `, '');
                    const scenarioOption = {
                        value: scenario.title,
                        description: scenario.description,
                        title: scenario.title,
                        commandTemplate: scenario.commandTemplate,
                        placeholders: scenario.placeholders,
                        placeholdersText: scenario.placeholdersText,
                        examples: scenario.examples || [],
                        label: displayTitle,
                        learnLink: scenario.learnLink,
                        category: scenario.category,
                    };
                    setSelectedScenario(scenarioOption);
                }, 0);
            }
        }

        const matching = recent.find((r) => r.title === last);
        if (matching?.inputs) setInputs(matching.inputs);
        if (matching?.routingDomain) {
            setRoutingDomain(matching.routingDomain);
            localStorage.setItem(ROUTING_KEY, matching.routingDomain);
        } else if (storedRouting) {
            setRoutingDomain(storedRouting);
        }
    }, [categories]);

    useEffect(() => {
        if (selectedScenario) {
            const match = recentScenarios.find((h) => h.title === selectedScenario.title);
            if (match?.inputs) {
                setInputs(match.inputs);
                validateAllFields(match.inputs, routingDomain);
            } else {
                setInputs({});
                setInputErrors({});
            }
        }
    }, [selectedScenario, recentScenarios, routingDomain]);

    useEffect(() => {
        if (selectedScenario && pendingEntry) {
            setInputs(pendingEntry.inputs);
            setRoutingDomain(pendingEntry.routingDomain || '');
            validateAllFields(pendingEntry.inputs, pendingEntry.routingDomain || '');
            setPendingEntry(null);
        }
    }, [selectedScenario, pendingEntry]);

    const handleInputChange = (placeholder, value) => {
        setInputs((prevInputs) => {
            const updatedInputs = { ...prevInputs, [placeholder]: value };

            if (selectedScenario) {
                const err = validateField(placeholder, value);
                setInputErrors((prev) => ({ ...prev, [placeholder]: err }));
            }

            return updatedInputs;
        });
    };

    const getEffectiveRoutingAddress = () => {
        const userEmail = inputs['<UserEmail>'] || '';
        const [prefix, domainPart] = userEmail.split('@');
        let domain = 'toolify365.mail.onmicrosoft.com';
        if (routingDomain.trim()) {
            domain = routingDomain.trim();
        } else if (domainPart) {
            const tenantBase = domainPart.split('.')[0];
            domain = `${tenantBase}.mail.onmicrosoft.com`;
        }
        const localPart = prefix || 'newuser';
        return `${localPart}@${domain}`;
    };

    const generateCommand = () => {
        let cmd = selectedScenario.commandTemplate;
        const routingAddress = getEffectiveRoutingAddress();
        cmd = cmd.replace(/<RoutingAddress>/g, routingAddress);
        selectedScenario.placeholders.forEach((ph) => {
            const val = inputs[ph] || ph;
            cmd = cmd.replace(new RegExp(ph, 'g'), val);
        });
        return cmd;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generateCommand());
        toast.info('Copied!');
    };


    return (
        <div className="container-xl mt-4">
            <h2 className="mb-3">Hybrid Recipient Management</h2>

            <div className="card mb-4">
                <div className="card-body">
                    <label className="form-label">Remote Routing Domain:</label>
                    <input
                        type="text"
                        className={`form-control mb-2 ${inputErrors.__routing ? 'is-invalid' : ''}`}
                        placeholder="e.g., toolify365.mail.onmicrosoft.com"
                        value={routingDomain}
                        onChange={(e) => {
                            const val = e.target.value;
                            setRoutingDomain(val);

                            const isValid =
                                val.trim() === '' || /^[a-zA-Z0-9-]+\.(mail\.onmicrosoft\.com)$/.test(val.trim());

                            setInputErrors((prev) => ({
                                ...prev,
                                __routing: isValid ? null : 'Ungültiges Format. Beispiel: toolify365.mail.onmicrosoft.com',
                            }));
                        }}
                    />
                    {inputErrors.__routing && (
                        <div className="invalid-feedback">{inputErrors.__routing}</div>
                    )}
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3 mb-md-0">
                            <label className="form-label">Select Category:</label>
                            <Select
                                options={categories}
                                value={selectedCategory}
                                onChange={(option) => {
                                    setSelectedCategory(option);
                                    setSelectedScenario(null);
                                    setInputs({});
                                    setInputErrors({});
                                }}
                                placeholder="Select a category..."
                                isClearable
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Select Scenario:</label>
                            <Select
                                options={filteredScenarios}
                                value={selectedScenario}
                                onChange={(option) => {
                                    setSelectedScenario(option);
                                    if (option) {
                                        localStorage.setItem(LAST_KEY, option.title);
                                    }
                                }}
                                placeholder={selectedCategory ? "Select a scenario..." : "Please select a category first"}
                                isDisabled={!selectedCategory}
                                isClearable
                            />
                        </div>
                    </div>
                </div>
            </div>

            {selectedScenario && (
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <h3 className="card-title mb-1">{selectedScenario.title}</h3>
                                <p className="text-muted mb-0">{selectedScenario.description}</p>
                            </div>
                            {selectedScenario.learnLink && (
                                <a
                                    href={selectedScenario.learnLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-primary ms-3 d-flex align-items-center"
                                    title="More information on Microsoft Learn"
                                >
                                    <IconBrandWindowsFilled size={18} className="me-2" />
                                    Open Microsoft Learn
                                </a>
                            )}
                        </div>

                        {selectedScenario.placeholders.map((ph, idx) => {
                            const currentValue = inputs[ph] || '';

                            const hasExamples = selectedScenario.examples?.some((ex) => ex.placeholder === ph);
                            const exampleOptions = selectedScenario.examples
                                ?.filter((ex) => ex.placeholder === ph)
                                .map((ex) => ({ label: ex.label, value: ex.value }));

                            return (
                                <div className="mb-3" key={idx}>
                                    <label className="form-label">{ph.replace(/[<>]/g, '')}:</label>

                                    {hasExamples ? (
                                        <Select
                                            classNamePrefix="select"
                                            isClearable
                                            isSearchable
                                            placeholder="Select or type..."
                                            options={exampleOptions}
                                            value={
                                                exampleOptions.find((opt) => opt.value === currentValue) ||
                                                (currentValue ? { label: currentValue, value: currentValue } : null)
                                            }
                                            onChange={(option) => handleInputChange(ph, option ? option.value : '')}
                                            onInputChange={(inputValue, { action }) => {
                                                if (action === 'input-change') {
                                                    handleInputChange(ph, inputValue);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            className={`form-control ${inputErrors[ph] ? 'is-invalid' : ''}`}
                                            value={currentValue}
                                            onChange={(e) => handleInputChange(ph, e.target.value)}
                                            placeholder={selectedScenario.placeholdersText?.[ph] || ''}
                                        />
                                    )}

                                    {inputErrors[ph] && (
                                        <div className="invalid-feedback">{inputErrors[ph]}</div>
                                    )}
                                </div>
                            );
                        })}

                        {allInputsValidOrEmpty() ? (
                            <>
                                <pre className="p-3 rounded mb-3 border bg-light text-dark border">
                                    {generateCommand()}
                                </pre>

                                <button className="btn btn-primary me-2" onClick={handleCopy}>
                                    Copy Command
                                </button>

                                <button
                                    className="btn btn-outline-success"
                                    onClick={() => {
                                        updateRecentScenarios(selectedScenario.title, inputs, routingDomain);
                                        localStorage.setItem(LAST_KEY, selectedScenario.title);
                                        localStorage.setItem(ROUTING_KEY, routingDomain);
                                        toast.success('Scenario saved!');
                                    }}
                                >
                                    <IconStar size={16} className="me-1" /> Save for later
                                </button>
                            </>
                        ) : (
                            <div className="alert d-flex align-items-center gap-2 px-3 py-2 rounded border bg-light border-muted text-dark">
                                <i className="ti ti-terminal-2 text-muted" style={{ fontSize: '1.2rem' }} />
                                <div className="small">
                                    <strong>PowerShell command cannot be generated.</strong> Please correct the highlighted fields with incomplete or invalid inputs.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {recentScenarios.length > 0 && (
                <div className="card mt-4 mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="card-title mb-0">Recent Scenarios</h4>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                    localStorage.removeItem(HISTORY_KEY);
                                    localStorage.removeItem(LAST_KEY);
                                    localStorage.removeItem(ROUTING_KEY);
                                    setRecentScenarios([]);
                                }}
                            >
                                <i className="ti ti-trash me-1" />Reset Scenarios
                            </button>
                        </div>

                        <div className="d-flex gap-3 flex-wrap">
                            {recentScenarios.map((entry, idx) => {
                                const scenario = scenarios.find((s) => s.title === entry.title);
                                return scenario ? (
                                    <div
                                        key={idx}
                                        className="card cursor-pointer"
                                        style={{ width: '18rem', cursor: 'pointer' }}
                                        onClick={() => {
                                            setPendingEntry(entry);
                                            if (scenario) {
                                                // Setze zuerst die Kategorie
                                                const category = categories.find(cat => cat.value === scenario.category);
                                                if (category) {
                                                    setSelectedCategory(category);
                                                }

                                                // Dann setze das Szenario
                                                setTimeout(() => {
                                                    // Entferne den Kategorie-Präfix aus dem Label
                                                    const displayTitle = scenario.title.replace(`${scenario.category}: `, '');
                                                    const scenarioOption = {
                                                        value: scenario.title,
                                                        description: scenario.description,
                                                        title: scenario.title,
                                                        commandTemplate: scenario.commandTemplate,
                                                        placeholders: scenario.placeholders,
                                                        placeholdersText: scenario.placeholdersText,
                                                        examples: scenario.examples || [],
                                                        label: displayTitle,
                                                        learnLink: scenario.learnLink,
                                                        category: scenario.category,
                                                    };
                                                    setSelectedScenario(scenarioOption);
                                                }, 0);

                                                localStorage.setItem(LAST_KEY, scenario.title);
                                                localStorage.setItem(ROUTING_KEY, entry.routingDomain || '');
                                            }
                                        }}
                                    >
                                        <div className="card-body">
                                            <h5 className="card-title">{scenario.title}</h5>
                                            <p className="card-text text-muted small mb-1">{scenario.description}</p>
                                            <ul className="list-unstyled small mb-0">
                                                {Object.entries(entry.inputs || {}).slice(0, 3).map(([key, val], i) => (
                                                    <li key={i}><strong>{key.replace(/[<>]/g, '')}:</strong> {val || <em>(leer)</em>}</li>
                                                ))}
                                                {Object.keys(entry.inputs || {}).length > 3 && <li>…</li>}
                                            </ul>
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};